// File: src/theme/theme.ts

import {createTheme} from '@mui/material/styles';

const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#FF9700',
            light: '#FFB74D',
            dark: '#F57C00',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#03DAC6',
            light: '#66FFF9',
            dark: '#00A896',
            contrastText: '#000000',
        },
        error: {
            main: '#CF6679',
            light: '#FF5983',
            dark: '#B00020',
            contrastText: '#FFFFFF',
        },
        warning: {
            main: '#FF9800',
            light: '#FFB74D',
            dark: '#F57C00',
            contrastText: '#000000',
        },
        info: {
            main: '#2196F3',
            light: '#64B5F6',
            dark: '#1976D2',
            contrastText: '#FFFFFF',
        },
        success: {
            main: '#4CAF50',
            light: '#81C784',
            dark: '#388E3C',
            contrastText: '#FFFFFF',
        },
        background: {
            default: '#121212',
            paper: 'rgba(30, 30, 30, 0.8)',
        },
        text: {
            primary: '#FFFFFF',
            secondary: 'rgba(255, 255, 255, 0.7)',
            disabled: 'rgba(255, 255, 255, 0.5)',
        },
        divider: '#404040',
    },
    typography: {
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
        ].join(','),
        h1: {
            fontSize: '2.5rem',
            fontWeight: 600,
            letterSpacing: '-0.01562em',
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 600,
            letterSpacing: '-0.00833em',
        },
        h3: {
            fontSize: '1.75rem',
            fontWeight: 500,
            letterSpacing: '0em',
        },
        h4: {
            fontSize: '1.5rem',
            fontWeight: 500,
            letterSpacing: '0.00735em',
        },
        h5: {
            fontSize: '1.25rem',
            fontWeight: 500,
            letterSpacing: '0em',
        },
        h6: {
            fontSize: '1.125rem',
            fontWeight: 600,
            letterSpacing: '0.0075em',
        },
        body1: {
            fontSize: '1rem',
            fontWeight: 400,
            letterSpacing: '0.00938em',
            lineHeight: 1.6,
        },
        body2: {
            fontSize: '0.875rem',
            fontWeight: 400,
            letterSpacing: '0.01071em',
            lineHeight: 1.5,
        },
    },
    shape: {
        borderRadius: 16,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#404040 #121212',
                    '&::-webkit-scrollbar': {
                        width: 8,
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#121212',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#404040',
                        borderRadius: 4,
                        '&:hover': {
                            backgroundColor: '#FF9700',
                        },
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: ({theme}) => ({
                    backgroundColor: 'rgba(30, 30, 30, 0.8)',
                    backdropFilter: 'blur(15px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        backgroundColor: 'rgba(35, 35, 35, 0.85)',
                        borderColor: 'rgba(255, 151, 0, 0.3)',
                        boxShadow: `0 8px 32px rgba(255, 151, 0, 0.15)`,
                        transform: 'translateY(-2px)',
                    },
                }),
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: ({theme}) => ({
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(30, 30, 30, 0.6)',
                        backdropFilter: 'blur(10px)',
                        '& fieldset': {
                            borderColor: '#404040',
                            transition: 'border-color 0.3s ease',
                        },
                        '&:hover fieldset': {
                            borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main,
                            boxShadow: `0 0 0 2px rgba(255, 151, 0, 0.2)`,
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-focused': {
                            color: theme.palette.primary.main,
                        },
                    },
                }),
            },
        },
        MuiButton: {
            styleOverrides: {
                root: () => ({
                    borderRadius: 12,
                    textTransform: 'none',
                    fontWeight: 600,
                    padding: '10px 24px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }),
                contained: ({theme}) => ({
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    boxShadow: `0 4px 16px rgba(255, 151, 0, 0.3)`,
                    '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        boxShadow: `0 8px 24px rgba(255, 151, 0, 0.4)`,
                        transform: 'translateY(-2px)',
                    },
                }),
                outlined: ({theme}) => ({
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    backgroundColor: 'rgba(255, 151, 0, 0.1)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 151, 0, 0.2)',
                        borderColor: theme.palette.primary.light,
                        transform: 'translateY(-1px)',
                    },
                }),
                text: ({theme}) => ({
                    color: theme.palette.primary.main,
                    '&:hover': {
                        backgroundColor: 'rgba(255, 151, 0, 0.1)',
                    },
                }),
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: () => ({
                    backgroundColor: 'rgba(30, 30, 30, 0.6)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px !important',
                    marginBottom: 16,
                    '&:before': {
                        display: 'none',
                    },
                    '&.Mui-expanded': {
                        margin: '0 0 16px 0',
                        backgroundColor: 'rgba(35, 35, 35, 0.8)',
                        borderColor: 'rgba(255, 151, 0, 0.3)',
                        boxShadow: `0 4px 20px rgba(255, 151, 0, 0.1)`,
                    },
                }),
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: () => ({
                    borderRadius: 12,
                    '&:hover': {
                        backgroundColor: 'rgba(255, 151, 0, 0.1)',
                    },
                    '&.Mui-expanded': {
                        backgroundColor: 'rgba(255, 151, 0, 0.15)',
                    },
                }),
                content: {
                    '&.Mui-expanded': {
                        margin: '12px 0',
                    },
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    padding: '16px 24px 24px',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: ({theme}) => ({
                    backgroundColor: 'rgba(255, 151, 0, 0.2)',
                    color: theme.palette.primary.main,
                    border: '1px solid rgba(255, 151, 0, 0.3)',
                    backdropFilter: 'blur(8px)',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 151, 0, 0.3)',
                    },
                }),
                colorPrimary: ({theme}) => ({
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                    },
                }),
                colorSecondary: ({theme}) => ({
                    backgroundColor: 'rgba(3, 218, 198, 0.2)',
                    color: theme.palette.secondary.main,
                    border: '1px solid rgba(3, 218, 198, 0.3)',
                }),
                outlined: ({theme}) => ({
                    backgroundColor: 'transparent',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: theme.palette.text.primary,
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                }),
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: () => ({
                    backgroundColor: 'rgba(30, 30, 30, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: `0 24px 48px rgba(0, 0, 0, 0.8)`,
                }),
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: ({theme}) => ({
                    backgroundColor: 'rgba(255, 151, 0, 0.1)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    color: theme.palette.text.primary,
                }),
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: ({theme}) => ({
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#404040',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px rgba(255, 151, 0, 0.2)`,
                    },
                }),
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: () => ({
                    backgroundColor: 'transparent',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 151, 0, 0.1)',
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 151, 0, 0.2)',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 151, 0, 0.3)',
                        },
                    },
                }),
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: ({theme}) => ({
                    '& .MuiSwitch-switchBase': {
                        '&.Mui-checked': {
                            color: theme.palette.primary.main,
                            '& + .MuiSwitch-track': {
                                backgroundColor: theme.palette.primary.main,
                                opacity: 0.5,
                            },
                        },
                    },
                    '& .MuiSwitch-track': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    },
                }),
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: () => ({
                    backgroundColor: 'rgba(30, 30, 30, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 12,
                }),
                standardInfo: () => ({
                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                    borderColor: 'rgba(33, 150, 243, 0.3)',
                    color: '#64B5F6',
                }),
                standardSuccess: () => ({
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    borderColor: 'rgba(76, 175, 80, 0.3)',
                    color: '#81C784',
                }),
                standardWarning: () => ({
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    borderColor: 'rgba(255, 152, 0, 0.3)',
                    color: '#FFB74D',
                }),
                standardError: () => ({
                    backgroundColor: 'rgba(207, 102, 121, 0.1)',
                    borderColor: 'rgba(207, 102, 121, 0.3)',
                    color: '#FF5983',
                }),
            },
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    backdropFilter: 'blur(4px)',
                },
            },
        },
    },
});

export default theme;
